import { createSelector } from '@ngrx/store';
import { CardStatus, OperationStatus } from 'src/graphql/generated/graphql.generated';
import { isNullOrEmpty, isNullOrWhitespace } from 'src/util/string';
import { selectAllCardEntities } from '../generated/card-record.generated';
import { selectAllCardSummaryEntities } from '../generated/card-summary-record.generated';
import {
  RequestPatientRecord,
  selectAllRequestPatientEntities,
} from '../generated/request-patient-record.generated';
import {
  selectVehicleControlZoneEntitiesByCompanyId,
  selectVehicleControlZoneEntitiesByControlZoneId,
} from '../generated/vehicle-control-zone-record.generated';
import {
  selectBaseControlZoneEntitiesByCompanyId,
  selectBaseControlZoneEntitiesByControlZoneId,
} from '../generated/base-control-zone-record.generated';
import { selectAllUnitSummaryEntities } from '../generated/unit-summary-record.generated';
import { selectAllCards, selectAllCardSummaries } from './entity-store-selectors';

export function selectCardByOperationId(operationId: string) {
  return createSelector(selectAllCards, (cards) =>
    cards.find((c) => c.operationId === operationId),
  );
}

export function selectCardByRequestId(requestId: string) {
  return createSelector(selectAllCards, (cards) => {
    return cards.find((c) => c.requestId === requestId);
  });
}

export function selectCardsByCompanyId(companyId: string) {
  return createSelector(selectAllCards, (cards) =>
    cards.valueSeq().filter((c) => c.companyId === companyId),
  );
}

export function selectCardByCardNumber(cardNumber: string) {
  return createSelector(selectAllCards, (cards) => {
    return cards.find((c) => c.cardNumber === cardNumber);
  });
}

export function selectCardById(id: string) {
  return createSelector(selectAllCards, (cards) => {
    return cards.get(id);
  });
}

export function selectCardSummariesByControlZoneCompanyId(companyId: string) {
  return createSelector(
    selectAllCardSummaryEntities,
    selectAllUnitSummaryEntities,
    selectVehicleControlZoneEntitiesByCompanyId(companyId),
    selectBaseControlZoneEntitiesByCompanyId(companyId),
    (cards, units, vehiclesControlZones, baseControlZones) => {
      const vids = vehiclesControlZones.map((v) => v.vehicleId);
      const baseIds = baseControlZones.map((b) => b.baseId);
      return cards.valueSeq().filter((c) => {
        const unit = units.find((u) => u.id === c.unitSummaryId);
        return (
          c.vehicleId != null &&
          (vids.includes(c.vehicleId) || (unit != null && baseIds.includes(unit.baseId)))
        );
      });
    },
  );
}

export function selectCardSummariesByControlZoneId(controlZoneId: string) {
  return createSelector(
    selectAllCardSummaryEntities,
    selectAllUnitSummaryEntities,
    selectVehicleControlZoneEntitiesByControlZoneId(controlZoneId),
    selectBaseControlZoneEntitiesByControlZoneId(controlZoneId),
    (cards, units, vehicleControlZones, baseControlZones) => {
      const vids = vehicleControlZones.map((v) => v.vehicleId);
      const baseIds = baseControlZones.map((b) => b.baseId);
      return cards.valueSeq().filter((c) => {
        const unit = units.find((u) => u.id === c.unitSummaryId);
        return (
          c.vehicleId != null &&
          (vids.includes(c.vehicleId) || (unit != null && baseIds.includes(unit.baseId)))
        );
      });
    },
  );
}

export function selectCardSummariesByRegions(regionIds: string[], ...status: CardStatus[]) {
  return createSelector(selectAllCardSummaries, (cards) => {
    return cards
      .filter((c) => regionIds.includes(c.regionId))
      .filter((c) => status.length === 0 || status.includes(c.status))
      .sortBy((c) => c.cardNumber)
      .valueSeq();
  });
}

export function selectCardSummariesByRegionId(regionId: string, ...status: CardStatus[]) {
  return createSelector(selectAllCardSummaries, (cards) => {
    const summaries = cards
      .filter((c) => status.length === 0 || status.includes(c.status))
      .filter((c) => c.regionId === regionId)
      .sortBy((c) => c.cardNumber);
    return summaries;
  });
}

export function selectNewWebOrderCardsByRegion(regionId: string) {
  return createSelector(selectCardSummariesByRegionId(regionId), (cards) => {
    return cards.filter(
      (c) =>
        c.apiWebhookSubscriptionId != null &&
        c.status === CardStatus.AwaitingAssignment &&
        c.webOrderAcknowledgedTime == null,
    );
  });
}

export function selectNewWebOrderCardsByRegions(regionIds: string[]) {
  return createSelector(selectCardSummariesByRegions(regionIds), (cards) => {
    return cards.filter(
      (c) =>
        c.apiWebhookSubscriptionId != null &&
        c.status === CardStatus.AwaitingAssignment &&
        c.webOrderAcknowledgedTime == null,
    );
  });
}

export function selectWebOrderStandbyActivationRequestedCardsByRegion(regionId: string) {
  return createSelector(selectCardSummariesByRegionId(regionId), (cards) => {
    return cards
      .filter((c) => c.apiWebhookSubscriptionId != null)
      .filter((c) => c.operation != null && c.operation.status === OperationStatus.OnStandby)
      .filter((c) => c.request != null && c.request.standbyActivationRequested === true);
  });
}

export function selectCardSummaryByUnitId(unitId: string) {
  return createSelector(selectAllCardSummaries, (cards) => {
    return cards.find((c) => c.unitId === unitId);
  });
}

export function selectCardsByPatientName(nameLike: string) {
  return createSelector(
    selectAllCardEntities,
    selectAllRequestPatientEntities,
    (cards, patients) => {
      if (isNullOrWhitespace(nameLike)) {
        return undefined;
      }
      const compare = (a1: string, a2: string) =>
        a1.toLocaleLowerCase().includes(a2.toLocaleLowerCase());
      const hasName = (p: RequestPatientRecord, first: string, last: string) =>
        isNullOrEmpty(last)
          ? compare(p.firstName ?? '', first) || compare(p.lastName ?? '', first)
          : compare(p.firstName ?? '', first) && compare(p.lastName ?? '', last);
      const [firstName, ...segments] = nameLike
        .split(' ')
        .map((n) => n.trim())
        .filter((n) => n !== '');
      const lastName = segments.join(' ');
      const reqIds = patients
        .filter((p) => hasName(p, firstName, lastName))
        .map((p) => p.requestId);
      return cards
        .filter((c) => (c.requestId == null ? false : reqIds.includes(c.requestId)))
        .toOrderedSet();
    },
  );
}

export function selectOperationCardSummariesByCommCenterIds(commCenterIds: string[]) {
  return createSelector(selectAllCardSummaries, (cards) => {
    return cards
      .filter(
        (c) =>
          c.status === CardStatus.AwaitingLaunch ||
          (c.status === CardStatus.Underway &&
            c.communicationCenterId != null &&
            commCenterIds.includes(c.communicationCenterId)),
      )
      .sortBy((c) => c.cardNumber)
      .valueSeq();
  });
}
